import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, FileText, FileUp, Loader2, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  artigosControllerTranscrever,
  getArtigosControllerDetalharQueryKey,
  getArtigosControllerListarQueryKey,
  useArtigosControllerAtualizar,
  useArtigosControllerCriar,
  useArtigosControllerDetalhar,
} from '@/api/generated/assessoria/assessoria';
import type { CriarArtigoDtoStatus } from '@/api/generated/model';
import { aplicarErrosDoServidor } from '@/api/erros';
import { ApiError } from '@/api/http-client';
import { VerPdfOriginal } from '@/features/discussoes/components/VerPdfOriginal';
import { Campo, CLASSE_CONTROLE } from '@/shared/components/Campo';
import { TextoMarkdown } from '@/shared/components/TextoMarkdown';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog';
import { Button } from '@/shared/ui/button';
import { Checkbox } from '@/shared/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Skeleton } from '@/shared/ui/skeleton';
import { toast } from '@/shared/ui/sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs';
import { Textarea } from '@/shared/ui/textarea';

const schema = z.object({
  titulo: z.string().trim().min(3, 'Informe um título com ao menos 3 caracteres').max(200),
  conteudo: z.string().trim().min(1, 'Escreva o texto do artigo'),
  status: z.enum(['RASCUNHO', 'PUBLICADO']),
});

type Formulario = z.infer<typeof schema>;

/** O PDF ligado ao artigo — vindo de uma importação agora ou de um salvamento anterior. */
interface PdfDoArtigo {
  arquivoId: string;
  pdfUrl: string | null;
  /** Importado AGORA: a transcrição ainda não foi revisada por ninguém. */
  novo: boolean;
  formatadoPorIa: boolean;
  paginas: number | null;
}

interface Props {
  aberto: boolean;
  aoFechar: () => void;
  /** Ausente = artigo novo. */
  artigoId?: string;
}

/**
 * Criar/editar artigo do advisor.
 *
 * Importar de PDF NÃO salva nada: a API guarda o PDF e devolve o texto, que
 * cai no campo para o advisor revisar. O "Salvar" só libera depois de ele
 * marcar que revisou — e a API confere de novo (`revisado: true`), porque o
 * texto vai para outro profissional ler como se fosse do advisor.
 */
export function ArtigoFormDialogo({ aberto, aoFechar, artigoId }: Props) {
  const queryClient = useQueryClient();
  const criar = useArtigosControllerCriar();
  const atualizar = useArtigosControllerAtualizar();
  const existente = useArtigosControllerDetalhar(artigoId ?? '', {
    query: { enabled: !!artigoId && aberto },
  });

  const [pdf, setPdf] = useState<PdfDoArtigo | null>(null);
  const [revisado, setRevisado] = useState(false);
  const [transcrevendo, setTranscrevendo] = useState(false);
  const [aba, setAba] = useState('editar');
  /** PDF escolhido com texto já no campo: espera a confirmação de substituir. */
  const [substituindoPor, setSubstituindoPor] = useState<File | null>(null);
  const inputPdf = useRef<HTMLInputElement>(null);

  const {
    register,
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    watch,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<Formulario>({
    resolver: zodResolver(schema),
    defaultValues: { titulo: '', conteudo: '', status: 'PUBLICADO' },
  });

  useEffect(() => {
    if (!aberto) return;
    setRevisado(false);
    setAba('editar');
    if (!artigoId) {
      reset({ titulo: '', conteudo: '', status: 'PUBLICADO' });
      setPdf(null);
    }
  }, [aberto, artigoId, reset]);

  const artigo = existente.data;
  useEffect(() => {
    if (!artigo) return;
    reset({ titulo: artigo.titulo, conteudo: artigo.conteudo, status: artigo.status });
    setPdf(
      artigo.arquivoId
        ? { arquivoId: artigo.arquivoId, pdfUrl: artigo.pdfUrl, novo: false, formatadoPorIa: true, paginas: null }
        : null,
    );
  }, [artigo, reset]);

  const conteudo = watch('conteudo');
  const precisaRevisar = pdf?.novo === true;

  /**
   * Chamada direta, com o "carregando" em estado próprio — mesmo motivo do
   * `SugestaoDeTexto`: com `useMutation`, o `isPending` ficava preso no
   * StrictMode e o diálogo travava.
   */
  async function importarPdf(arquivo: File) {
    setSubstituindoPor(null);
    setTranscrevendo(true);
    try {
      const resultado = await artigosControllerTranscrever({ arquivo });
      setValue('conteudo', resultado.conteudo, { shouldValidate: true, shouldDirty: true });
      if (!getValues('titulo').trim() && resultado.tituloSugerido) {
        setValue('titulo', resultado.tituloSugerido, { shouldValidate: true, shouldDirty: true });
      }
      setPdf({
        arquivoId: resultado.arquivoId,
        pdfUrl: resultado.pdfUrl,
        novo: true,
        formatadoPorIa: resultado.formatadoPorIa,
        paginas: resultado.paginas,
      });
      setRevisado(false);
      setAba('editar');
      toast.success('PDF transcrito. Revise o texto antes de salvar.');
    } catch (erro) {
      if (erro instanceof ApiError && erro.status === 503) {
        toast.error(erro.message || 'A IA não está disponível agora. Tente novamente mais tarde.');
      } else {
        toast.error(erro instanceof ApiError ? erro.message : 'Não foi possível transcrever o PDF.');
      }
    } finally {
      setTranscrevendo(false);
      if (inputPdf.current) inputPdf.current.value = '';
    }
  }

  const aoEnviar = handleSubmit(async (dados) => {
    const corpo = {
      titulo: dados.titulo,
      conteudo: dados.conteudo,
      status: dados.status as CriarArtigoDtoStatus,
      arquivoId: pdf?.arquivoId ?? null,
      // PDF que já estava no artigo foi revisado quando o artigo foi salvo.
      ...(pdf ? { revisado: pdf.novo ? revisado : true } : {}),
    };

    try {
      if (artigoId) {
        await atualizar.mutateAsync({ id: artigoId, data: corpo });
        void queryClient.invalidateQueries({ queryKey: getArtigosControllerDetalharQueryKey(artigoId) });
      } else {
        await criar.mutateAsync({ data: corpo });
      }
      void queryClient.invalidateQueries({ queryKey: getArtigosControllerListarQueryKey() });
      toast.success(dados.status === 'PUBLICADO' ? 'Artigo publicado.' : 'Rascunho salvo.');
      aoFechar();
    } catch (erro) {
      aplicarErrosDoServidor(erro, setError, 'Não foi possível salvar o artigo.');
    }
  });

  const carregandoExistente = !!artigoId && existente.isPending;
  const ocupado = isSubmitting || transcrevendo;

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && !ocupado && aoFechar()}>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle>{artigoId ? 'Editar artigo' : 'Novo artigo'}</DialogTitle>
          <DialogDescription>
            Os artigos publicados aparecem para o médico no rodapé das discussões que você atende.
          </DialogDescription>
        </DialogHeader>

        {carregandoExistente ? (
          <div className="space-y-3">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-72 w-full" />
          </div>
        ) : (
          <form onSubmit={aoEnviar} className="space-y-4" noValidate>
            {errors.root?.message && (
              <Alert variant="destructive">
                <AlertDescription>{errors.root.message}</AlertDescription>
              </Alert>
            )}

            {precisaRevisar && (
              <Alert className="border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100">
                <AlertTriangle className="h-4 w-4 !text-amber-600" />
                <AlertDescription>
                  <strong>Revise a transcrição antes de salvar.</strong>{' '}
                  {pdf.formatadoPorIa
                    ? 'O texto foi extraído do PDF e organizado pela IA — confira se nada foi omitido ou alterado.'
                    : 'A IA não estava disponível: este é o texto extraído cru do PDF. Ajuste quebras de linha e títulos.'}
                </AlertDescription>
              </Alert>
            )}

            <Campo id="titulo" rotulo="Título" erro={errors.titulo?.message} obrigatorio>
              <Input id="titulo" className={CLASSE_CONTROLE} placeholder="Título do artigo" {...register('titulo')} />
            </Campo>

            <div className="flex flex-wrap items-center gap-2">
              <input
                ref={inputPdf}
                type="file"
                accept="application/pdf"
                className="hidden"
                data-testid="input-pdf-artigo"
                onChange={(e) => {
                  const arquivo = e.target.files?.[0];
                  if (!arquivo) return;
                  if (getValues('conteudo').trim()) setSubstituindoPor(arquivo);
                  else void importarPdf(arquivo);
                }}
              />
              <Button
                type="button"
                variant="outline"
                className="gap-2"
                disabled={ocupado}
                onClick={() => inputPdf.current?.click()}
              >
                {transcrevendo ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />}
                {pdf ? 'Importar outro PDF' : 'Importar de PDF'}
              </Button>

              {pdf && (
                <div className="flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs text-muted-foreground">
                  <FileText className="h-3.5 w-3.5 text-brand" />
                  PDF original{pdf.paginas ? ` · ${pdf.paginas} pág.` : ''}
                  <VerPdfOriginal key={pdf.arquivoId} url={pdf.pdfUrl} rotulo="Ver" className="ml-1 h-6 px-2 text-xs" />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    title="Desvincular o PDF (o texto continua)"
                    disabled={ocupado}
                    onClick={() => {
                      setPdf(null);
                      setRevisado(false);
                    }}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              )}

              {transcrevendo && (
                <span className="text-xs text-muted-foreground">
                  Transcrevendo o PDF… artigos longos podem levar alguns minutos.
                </span>
              )}
            </div>

            <Tabs value={aba} onValueChange={setAba}>
              <div className="flex items-center justify-between gap-2">
                <TabsList>
                  <TabsTrigger value="editar">Editar</TabsTrigger>
                  <TabsTrigger value="previa">Pré-visualizar</TabsTrigger>
                </TabsList>
                <span className="text-xs text-muted-foreground">
                  {conteudo.length.toLocaleString('pt-BR')} caracteres · aceita Markdown (# título, **negrito**, - lista)
                </span>
              </div>

              <TabsContent value="editar">
                <Campo id="conteudo" rotulo="Texto" erro={errors.conteudo?.message} obrigatorio>
                  <Textarea
                    id="conteudo"
                    className={`${CLASSE_CONTROLE} min-h-[45vh] font-mono text-sm`}
                    placeholder="Escreva o artigo ou importe de um PDF"
                    disabled={transcrevendo}
                    {...register('conteudo')}
                  />
                </Campo>
              </TabsContent>
              <TabsContent value="previa">
                <div className="min-h-[45vh] max-h-[55vh] overflow-y-auto rounded-md border border-border p-4">
                  {conteudo.trim() ? (
                    <TextoMarkdown texto={conteudo} />
                  ) : (
                    <p className="text-sm text-muted-foreground">Nada para pré-visualizar ainda.</p>
                  )}
                </div>
              </TabsContent>
            </Tabs>

            <Campo id="status" rotulo="Visibilidade" dica="Rascunho fica só para você; publicado aparece nas discussões.">
              <Controller
                control={control}
                name="status"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger id="status" className={`${CLASSE_CONTROLE} w-60`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PUBLICADO">Publicado</SelectItem>
                      <SelectItem value="RASCUNHO">Rascunho</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </Campo>

            {precisaRevisar && (
              <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50/60 p-3 dark:border-amber-800 dark:bg-amber-950/40">
                <Checkbox
                  id="revisado"
                  checked={revisado}
                  onCheckedChange={(v) => setRevisado(v === true)}
                  className="mt-0.5"
                />
                <Label htmlFor="revisado" className="text-sm font-normal leading-snug">
                  Revisei a transcrição e confirmo que o texto está correto.
                </Label>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={aoFechar} disabled={ocupado}>
                Cancelar
              </Button>
              <Button type="submit" disabled={ocupado || (precisaRevisar && !revisado)}>
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Salvar
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>

      <AlertDialog
        open={!!substituindoPor}
        onOpenChange={(v) => {
          if (v) return;
          setSubstituindoPor(null);
          if (inputPdf.current) inputPdf.current.value = '';
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Substituir o texto?</AlertDialogTitle>
            <AlertDialogDescription>
              O texto atual será trocado pela transcrição de &quot;{substituindoPor?.name}&quot;.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => substituindoPor && void importarPdf(substituindoPor)}>
              Substituir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
